import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface FrameworkOption {
  readonly frameworkId: string;
  readonly frameworkCode: string;
  readonly frameworkName: string;
}

interface ControlRow {
  readonly controlId: string;
  readonly controlCode: string;
  readonly controlName: string;
  readonly controlType: string;
  readonly implementationStatus: string;
  readonly assessmentStatus: string;
  readonly complianceStatus: string;
}

const COMPLIANCE_STATUSES = ['', 'NOT_ASSESSED', 'COMPLIANT', 'NON_COMPLIANT', 'PARTIALLY_COMPLIANT', 'NOT_APPLICABLE'];
const ASSESS_STATUSES = ['COMPLIANT', 'NON_COMPLIANT', 'PARTIALLY_COMPLIANT', 'NOT_APPLICABLE'];

/**
 * M30 — Control Assessment Screen (§16 / §31). Select a compliance framework
 * and review each of its controls with implementation, assessment and
 * compliance verdicts. Assessors record findings and upload evidence
 * (retained per BR-04) that drives the framework's rolled-up compliance score —
 * assessment and evidence upload are wired to the guarded /v1/compliance
 * write API.
 */
export function M30ControlAssessmentPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [frameworks, setFrameworks] = useState<FrameworkOption[]>([]);
  const [frameworkId, setFrameworkId] = useState('');
  const [complianceStatus, setComplianceStatus] = useState('');
  const [controls, setControls] = useState<ControlRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  // assessment controls
  const [assessStatus, setAssessStatus] = useState('COMPLIANT');
  const [evidenceRef, setEvidenceRef] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    apiRequest<{ data: FrameworkOption[] }>('/v1/compliance/frameworks', { token })
      .then((r) => {
        if (!alive) return;
        setFrameworks(r.data);
        if (r.data.length > 0) setFrameworkId(r.data[0].frameworkId);
        setError(null);
      })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token]);

  useEffect(() => {
    if (!frameworkId) { setControls([]); return; }
    let alive = true;
    const params = new URLSearchParams();
    if (complianceStatus) params.set('complianceStatus', complianceStatus);
    const qs = params.toString();
    apiRequest<{ data: ControlRow[] }>(`/v1/compliance/frameworks/${frameworkId}/controls${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setControls(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token, frameworkId, complianceStatus, reload]);

  const failMessage = (err: unknown, verb: string, perm: string): string =>
    err instanceof ApiError && err.status === 403
      ? t('m30.control.notAuthorised', { verb, perm })
      : (err as Error).message;

  const runRowAction = async (
    fn: () => Promise<unknown>,
    successMsg: string,
    verb: string,
    perm: string,
  ): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await fn();
      setNotice(successMsg);
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, verb, perm));
    }
  };

  const assess = (c: ControlRow): Promise<void> =>
    runRowAction(
      () => apiRequest(`/v1/compliance/controls/${c.controlId}/assess`, { method: 'POST', body: { complianceStatus: assessStatus }, token }),
      t('m30.control.assessed', { code: c.controlCode }), t('m30.control.verbAssess'), 'M30_COMPLIANCE_MANAGE',
    );

  const uploadEvidence = (c: ControlRow): Promise<void> =>
    runRowAction(
      () => apiRequest(`/v1/compliance/controls/${c.controlId}/evidence`, { method: 'POST', body: { evidenceRef: evidenceRef.trim() || `evidence://${c.controlCode}` }, token }),
      t('m30.control.evidenceRecorded', { code: c.controlCode }), t('m30.control.verbEvidence'), 'M30_COMPLIANCE_MANAGE',
    );

  return (
    <main aria-label="control-assessment">
      <h1>{t('m30.control.title')}</h1>
      <div className="filters">
        <label>
          {t('m30.control.filterFramework')}
          <select aria-label="select-framework" value={frameworkId} onChange={(e) => setFrameworkId(e.target.value)}>
            {frameworks.map((f) => <option key={f.frameworkId} value={f.frameworkId}>{f.frameworkCode} — {f.frameworkName}</option>)}
          </select>
        </label>
        <label>
          {t('m30.control.filterCompliance')}
          <select aria-label="filter-compliance-status" value={complianceStatus} onChange={(e) => setComplianceStatus(e.target.value)}>
            {COMPLIANCE_STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('m30.control.all')}</option>)}
          </select>
        </label>
      </div>
      <div className="assessment-controls" aria-label="assessment-controls">
        <label>{t('m30.control.verdict')}
          <select aria-label="field-assess-status" value={assessStatus} onChange={(e) => setAssessStatus(e.target.value)}>
            {ASSESS_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label>{t('m30.control.evidenceRef')}<input aria-label="field-evidence-ref" value={evidenceRef} onChange={(e) => setEvidenceRef(e.target.value)} /></label>
      </div>
      {actionError && <p role="alert" aria-label="control-error">{actionError}</p>}
      {notice && <p aria-label="control-notice">{notice}</p>}
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m30.control.loading')}</p> : (
        <>
          <p aria-label="control-count">{t('m30.control.count', { count: controls.length })}</p>
          <table aria-label="control-table">
            <thead><tr><th>{t('m30.control.thCode')}</th><th>{t('m30.control.thName')}</th><th>{t('m30.control.thType')}</th><th>{t('m30.control.thImplementation')}</th><th>{t('m30.control.thAssessment')}</th><th>{t('m30.control.thCompliance')}</th><th>{t('m30.control.thActions')}</th></tr></thead>
            <tbody>
              {controls.map((c) => (
                <tr key={c.controlId}>
                  <td>{c.controlCode}</td><td>{c.controlName}</td><td>{c.controlType}</td>
                  <td>{c.implementationStatus}</td><td>{c.assessmentStatus}</td><td>{c.complianceStatus}</td>
                  <td>
                    <button type="button" aria-label={`assess-${c.controlCode}`} onClick={() => void assess(c)}>{t('m30.control.btnAssess')}</button>
                    <button type="button" aria-label={`evidence-${c.controlCode}`} onClick={() => void uploadEvidence(c)}>{t('m30.control.btnEvidence')}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
